import { matplotlibRampOptions } from '@/lib/colormaps';
import { defaultRampName } from '@/lib/cutFillConvention';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useAtom } from 'jotai';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { reportTrackingAtom } from '@/atoms/report-progress.atom';
import { execute } from '@/graphql/execute';
import { reportLocaleFor, SUPPORTED_REPORT_LOCALES } from '@/lib/reportLocales';
import { estimateRenderSeconds, formatDurationShort } from '@/lib/stationVolumes/perStationPdf';
import { useDialog } from '@/providers/DialogProvider';
import { startPdfReport } from '@/queries/report.query';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NumericField } from '@/components/ui/numeric-field';

/**
 * Which section the report is built from. Omitted, it is the deviation
 * section this dialog has always produced; `STATION_VOLUMES` is what the
 * station-volumes dock's "PDF report section" item asks for (ruling T15.6).
 * Modelled as a prop rather than an in-dialog picker because the caller
 * always knows which analysis the user is looking at — asking again would be
 * a question with one possible answer.
 */
export type ReportSectionRequest =
  | { type: 'DEVIATION' }
  | {
      type: 'STATION_VOLUMES';
      /** Internal stations; omit for the whole analysis. */
      stationFrom?: number | null;
      stationTo?: number | null;
    }
  | {
      /** One A4-landscape page per station bin (spec §13). */
      type: 'STATION_PAGES';
      stationFrom?: number | null;
      stationTo?: number | null;
      /** How many pages this will produce — the dock counted them, and the
       *  user should read the number before the job is queued and paid for. */
      binCount: number;
      /**
       * How many top-down renders the render job would draw — every bin in the
       * window, blank ones included, which is more than `binCount` whenever
       * the brush spans a bin that measured nothing. Only the render estimate
       * reads it; the scope line still quotes `binCount`, because that is how
       * many pages the PDF has.
       */
      renderCount: number;
      /**
       * Whether this run can produce per-station point-cloud renders (spec
       * §13.7) — the caller answers with
       * `stationVolumes/perStationPdf.ts::stationRendersAvailable`, which reads
       * the layer's own metadata. False hides the checkbox rather than
       * disabling it: a surface difference and a pre-addendum deviation run
       * can never produce renders, so there is nothing to offer.
       */
      rendersAvailable: boolean;
      /** Original deviation range, for the export-range placeholders. */
      renderRange?: [number, number] | null;
      /** Original render colormap, used as the export colormap default. */
      renderColormap?: string | null;
      /**
       * LAYER_STACK results only: WHICH of the stack's N−1 layers this
       * document prints (spec §7). The pages draw the whole stacked
       * cross-section either way; this chooses whose volumes go in the table,
       * and it is the dock's active chip. Absent for a single-pair run, which
       * has no layers to choose between — and the server refuses a stack
       * request that does not carry it, because there is no honest default.
       */
      stackLayerIndex?: number;
      /**
       * How many section overlays (Task 14's `overlayStatesById`) are still
       * `pending` — sampled server-side but not drawn yet. Every page this
       * job prints comes from the overlay bytes as they stand right now, so a
       * still-sampling overlay means the pages come out with no reference
       * line for it. Omitted or 0 renders no note.
       */
      overlaysPendingCount?: number;
    }
  | {
      /**
       * The multi-layer thickness report (spec §7). `layerId` is the STACK
       * RESULT layer; the document's shape — one stack summary plus one
       * station section per LAYER (N levels give N-1) — is assembled
       * server-side, so the dialog names the run and nothing else.
       *
       * The station window applies to EVERY layer, and it is the knob that
       * matters here: the server spends ONE 400-bin page budget across the
       * whole stack (ruling R16), so a six-level run has roughly 80 bins a
       * layer to play with and narrowing is how a deep stack fits.
       */
      type: 'LAYER_STACK';
      /** Internal stations, applied to EVERY layer; omit for the whole run. */
      stationFrom?: number | null;
      stationTo?: number | null;
      /** N−1: how many layers this stack has (the dock counted them). Shown
       *  in the scope line so a rejection is predictable rather than
       *  discovered — the 400-bin cap (ruling R16) is spent across every
       *  layer, not per layer. */
      layerCount: number;
      /** The cross-layer station-bin total (every layer's bins summed), not
       *  a per-layer count — that is the number the 400-bin cap actually
       *  compares against. */
      binCount: number;
    };

interface Props {
  projectId: string;
  layerId: string;
  layerName: string;
  /** Defaults to a DEVIATION section. */
  section?: ReportSectionRequest;
  /**
   * Human-readable station range for the summary line, e.g. `1+000.00 –
   * 1+500.00`. The caller supplies it because display stations only exist in
   * the bins it already holds.
   */
  stationRangeLabel?: string;
}

export function GenerateReportDialog({ projectId, layerId, layerName, section, stationRangeLabel }: Props) {
  const { t, i18n } = useTranslation('project');
  const { dismissActiveDialog } = useDialog();
  const [, setTrackers] = useAtom(reportTrackingAtom);
  const isStationVolumes = section?.type === 'STATION_VOLUMES';
  const layerStack = section?.type === 'LAYER_STACK' ? section : null;
  // Narrowed once, so the page count can be read without re-checking the tag
  // (and without a non-null assertion) everywhere it is printed.
  const stationPages = section?.type === 'STATION_PAGES' ? section : null;
  // Both station sections are scoped to a station window, so both get the
  // scope line below; only the copy inside it differs.
  const isStationScoped = isStationVolumes || stationPages !== null || layerStack !== null;

  const schema = useMemo(
    () =>
      z.object({
        title: z.string().min(1),
        notes: z.string().optional(),
        locale: z.enum(SUPPORTED_REPORT_LOCALES),
        /** Per-station point-cloud renders (spec §13.7). Only ever sent with a
         *  STATION_PAGES section; ignored by the other two. */
        renders: z.boolean(),
        renderColormap: z.string(),
        renderRangeMin: z.number().nullable(),
        renderRangeMax: z.number().nullable(),
      })
      .superRefine((data, ctx) => {
        if (!data.renders) return;
        const hasMin = data.renderRangeMin !== null;
        const hasMax = data.renderRangeMax !== null;
        if (hasMin !== hasMax) {
          const path = hasMin ? ['renderRangeMax'] : ['renderRangeMin'];
          ctx.addIssue({ code: 'custom', path, message: t('report.renderRangeBothRequired') });
          return;
        }
        if (hasMin && hasMax && !(data.renderRangeMin! < data.renderRangeMax!)) {
          ctx.addIssue({
            code: 'custom',
            path: ['renderRangeMin'],
            message: t('report.renderRangeMinLessThanMax'),
          });
        }
      }),
    [t],
  );
  type FormData = z.infer<typeof schema>;

  const currentLocale = reportLocaleFor(i18n.language);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: layerName,
      notes: '',
      locale: currentLocale,
      // Unticked: the renders cost roughly a second a page, so the cheap
      // document is the one the reader gets without asking for anything.
      renders: false,
      // The convention's diverging ramp, not `jet` (T4): the per-station
      // page prints this render beside a section drawing whose cut band is
      // red, and the two have to agree.
      renderColormap: stationPages?.renderColormap ?? defaultRampName('deviation'),
      renderRangeMin: null,
      renderRangeMax: null,
    },
  });

  // The ramps offered for the renders, plus — appended, never substituted —
  // whatever the run ACTUALLY used if that is no longer on the list (T4
  // dropped `jet` and the unreversed `RdBu`). A `<select>` whose value is not
  // among its options silently shows a different ramp than the one the job
  // will use, which is exactly the class of quiet disagreement F21 was about.
  const recorded = stationPages?.renderColormap;
  const rampOptions = matplotlibRampOptions('deviation');
  const renderColormapOptions =
    recorded && !rampOptions.some((o) => o.value === recorded)
      ? [...rampOptions, { value: recorded, label: recorded }]
      : rampOptions;

  const mutation = useMutation({
    mutationFn: (vars: FormData) =>
      execute(startPdfReport, {
        input: {
          projectId,
          locale: vars.locale,
          title: vars.title,
          notes: vars.notes || null,
          sections:
            section?.type === 'STATION_PAGES'
              ? [
                  {
                    type: 'STATION_PAGES' as const,
                    stationPages: {
                      layerId,
                      stationFrom: section.stationFrom ?? null,
                      stationTo: section.stationTo ?? null,
                      ...(typeof section.stackLayerIndex === 'number'
                        ? { stackLayerIndex: section.stackLayerIndex }
                        : {}),
                      renders: vars.renders,
                      ...(vars.renders ? { renderColormap: vars.renderColormap } : {}),
                      ...(vars.renders && vars.renderRangeMin !== null && vars.renderRangeMax !== null
                        ? { renderRangeMin: vars.renderRangeMin, renderRangeMax: vars.renderRangeMax }
                        : {}),
                    },
                  },
                ]
              : section?.type === 'STATION_VOLUMES'
                ? [
                    {
                      type: 'STATION_VOLUMES' as const,
                      stationVolumes: {
                        layerId,
                        stationFrom: section.stationFrom ?? null,
                        stationTo: section.stationTo ?? null,
                      },
                    },
                  ]
                : section?.type === 'LAYER_STACK'
                  ? [
                      {
                        type: 'LAYER_STACK' as const,
                        layerStack: {
                          layerId,
                          stationFrom: section.stationFrom ?? null,
                          stationTo: section.stationTo ?? null,
                        },
                      },
                    ]
                  : [{ type: 'DEVIATION' as const, deviation: { layerId } }],
        },
      }),
    onSuccess: (data, vars) => {
      const batchId = data.startPdfReport;
      // With renders the batch starts as a RENDER job that chains the PDF
      // (spec §13.7), so the toast opens on the render stage instead of
      // flashing "Generating PDF report" until the tracker's first poll. The
      // count is also what tells `useReportProgress` this tracker is the one
      // allowed to follow a RENDER job (a deviation run's render child sits in
      // the same batch as its own chained report).
      const renderCount = vars.renders && stationPages ? stationPages.renderCount : 0;
      const toastId = toast.loading(
        renderCount > 0
          ? t('report.renderingToast', { count: renderCount, title: vars.title })
          : t('report.runningToast', { title: vars.title }),
        { duration: Infinity },
      );
      setTrackers((prev) => [
        ...prev,
        {
          toastId,
          batchId,
          projectId,
          title: vars.title,
          pageCount: stationPages?.binCount,
          ...(renderCount > 0 ? { renderCount, stage: 'rendering' as const } : {}),
        },
      ]);
      dismissActiveDialog();
    },
    onError: (err: Error) => {
      toast.error(err.message || t('report.failedToast'));
    },
  });

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('report.dialogTitle')}</DialogTitle>
        <DialogDescription>
          {stationPages
            ? t('report.dialogDescriptionStationPages')
            : layerStack
              ? t('report.dialogDescriptionLayerStack')
              : isStationVolumes
                ? t('report.dialogDescriptionStationVolumes')
                : t('report.dialogDescription')}
        </DialogDescription>
      </DialogHeader>

      {/* Say what the PDF will contain before it is paid for and queued —
          a station-volumes report of the whole corridor and one of a brushed
          selection are different documents, a 251-page per-station PDF is a
          third one again, and a layer-stack report's page budget is spent
          across every layer, not per layer, so the layer and bin counts are
          the thing that predicts a rejection. */}
      {isStationScoped && (
        <p data-testid='report-section-scope' className='text-sm text-slate-500 dark:text-stone-400'>
          {stationPages
            ? stationRangeLabel
              ? t('report.stationPagesScopeRange', { count: stationPages.binCount, range: stationRangeLabel })
              : t('report.stationPagesScopeAll', { count: stationPages.binCount })
            : layerStack
              ? stationRangeLabel
                ? t('report.layerStackScopeRange', {
                    count: layerStack.binCount,
                    layerCount: layerStack.layerCount,
                    range: stationRangeLabel,
                  })
                : t('report.layerStackScopeAll', { count: layerStack.binCount, layerCount: layerStack.layerCount })
              : stationRangeLabel
                ? t('report.stationVolumesScopeRange', { range: stationRangeLabel })
                : t('report.stationVolumesScopeAll')}
        </p>
      )}

      {/* A still-`pending` overlay (Task 14) prints no reference line on any
          page of this run — the copy has to say so before the job is queued
          and paid for, not after the PDF comes back missing a line. */}
      {stationPages && (stationPages.overlaysPendingCount ?? 0) > 0 && (
        <p data-testid='sv-report-overlays-stale' className='text-xs text-amber-700 dark:text-amber-300'>
          {t('stationVolumes.section.overlays.reportPending', { n: stationPages.overlaysPendingCount })}
        </p>
      )}

      <Form {...form}>
        <form onSubmit={form.handleSubmit((v) => mutation.mutate(v))} className='flex flex-col gap-3'>
          <FormField
            control={form.control}
            name='title'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('report.titleLabel')}</FormLabel>
                <FormControl>
                  <Input type='text' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name='notes'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('report.notesLabel')}</FormLabel>
                <FormControl>
                  <Input type='text' {...field} />
                </FormControl>
              </FormItem>
            )}
          />

          {/* Only for a run that can actually produce them (§13.7): a surface
              difference has no coloured cloud, and a deviation run made before
              the bin polygons existed has nothing to frame the render on. No
              HTML `disabled` — the control is absent, not inert (CLAUDE.md). */}
          {stationPages?.rendersAvailable && (
            <div className='space-y-2'>
              <FormField
                control={form.control}
                name='renders'
                render={({ field }) => (
                  <FormItem data-testid='report-include-renders' className='flex flex-row items-start gap-2'>
                    <FormControl>
                      <Checkbox
                        className='mt-0.5'
                        checked={field.value}
                        onCheckedChange={(v) => field.onChange(v === true)}
                      />
                    </FormControl>
                    <div className='space-y-0.5'>
                      <FormLabel className='font-normal'>{t('report.includeRenders')}</FormLabel>
                      {/* The cost is named in the hint, not tacked onto the
                          label as a bare number: three lines above, the scope
                          line already quotes a count, and two unexplained
                          numbers meaning different things is how a reader is
                          misled. `FormDescription` (not a hand-rolled <p>)
                          carries the id `FormControl` points `aria-describedby`
                          at, so the sentence is announced with the checkbox. */}
                      <FormDescription className='text-xs'>
                        {t('report.includeRendersHint', {
                          duration: formatDurationShort(estimateRenderSeconds(stationPages.renderCount)),
                        })}
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {form.watch('renders') && (
                <div
                  data-testid='report-render-options'
                  className='space-y-3 rounded-md border border-slate-200 p-3 dark:border-stone-700'>
                  <FormField
                    control={form.control}
                    name='renderColormap'
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('deviationAnalysis.renderColormap')}</FormLabel>
                        <FormControl>
                          <select
                            className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                            {...field}>
                            {/* One list (`@/lib/colormaps`), oriented for a
                                DEVIATION run so red is the positive — cut —
                                end. `RdBu` used to be offered here unreversed,
                                which painted cut BLUE on the very page whose
                                section drawing paints it red (F21). */}
                            {renderColormapOptions.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                        </FormControl>
                        <FormDescription className='text-xs'>
                          {t('deviationAnalysis.renderColormapHelp')}
                        </FormDescription>
                      </FormItem>
                    )}
                  />

                  <div data-testid='report-render-range'>
                    <FormLabel>{t('report.renderRangeLabel')}</FormLabel>
                    <div className='mt-2 flex items-start gap-2'>
                      <FormField
                        control={form.control}
                        name='renderRangeMin'
                        render={({ field }) => (
                          <FormItem className='flex-1'>
                            <FormControl>
                              <NumericField
                                name={field.name}
                                step='0.001'
                                value={field.value}
                                onChange={field.onChange}
                                onClear={() => field.onChange(null)}
                                placeholder={String(stationPages.renderRange?.[0] ?? -0.05)}
                                aria-label={t('report.renderRangeMin')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <span className='pt-2 text-xs text-slate-500'>—</span>
                      <FormField
                        control={form.control}
                        name='renderRangeMax'
                        render={({ field }) => (
                          <FormItem className='flex-1'>
                            <FormControl>
                              <NumericField
                                name={field.name}
                                step='0.001'
                                value={field.value}
                                onChange={field.onChange}
                                onClear={() => field.onChange(null)}
                                placeholder={String(stationPages.renderRange?.[1] ?? 0.05)}
                                aria-label={t('report.renderRangeMax')}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormDescription className='mt-1 text-xs'>{t('report.renderRangeHelp')}</FormDescription>
                  </div>
                </div>
              )}
            </div>
          )}

          <FormField
            control={form.control}
            name='locale'
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('report.languageLabel')}</FormLabel>
                <FormControl>
                  <select
                    className='w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2'
                    {...field}>
                    <option value='en'>English</option>
                    <option value='de'>Deutsch</option>
                    <option value='es'>Español</option>
                    <option value='fr'>Français</option>
                    <option value='hu'>Magyar</option>
                  </select>
                </FormControl>
              </FormItem>
            )}
          />

          <DialogFooter>
            <Button variant='outline' type='button' onClick={() => dismissActiveDialog()} disabled={mutation.isPending}>
              {t('common.cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button type='submit' disabled={mutation.isPending}>
              {mutation.isPending ? t('report.generating') : t('report.generate')}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </>
  );
}
